import { useMessageModel } from "../store/use-message-model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faCircleNotch,
  faComments,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { MessageModelAttributes } from "../types/message-types";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";
import GetUser from "../utils/getUser";
import clsx from "clsx";
import moment from "moment";
import ModalPortal from "../components/modal-portal";

const MessageModel = () => {
  const user = GetUser();
  const messageInputRef = useRef<HTMLInputElement>();
  const messageList = useRef<HTMLDivElement>();
  const { isOpen, onOpen, onClose, initialValues } = useMessageModel();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit] = useState<number>(10);
  const [messages, setMessages] = useState<MessageModelAttributes[]>([]);
  const [value, setValue] = useState<string>(initialValues?.message || "");
  const [loading, setLoading] = useState<boolean>(false);

  // -------------------------- [ HANDLE SEND MESSAGE ] -----------------------------------
  const HandleSendMessage = async () => {
    setLoading(true);
    const newMessage: any = {
      chaseId: initialValues.id,
      userId: user.id,
      message: value,
      type: "dashboard",
    };

    setMessages((prev) => [...prev, newMessage]);
    try {
      messageInputRef.current.value = "";
      setValue("");
      await axiosInstance.post("/message/send", newMessage);

      if (initialValues.isRejected) {
        onOpen(initialValues.id, initialValues.title, "");
      }

      setLoading(false);
    } catch (error) {
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  // -------------------------- [ FETCH CHASE MESSAGES ] -----------------------------------
  useEffect(() => {
    const findMessages = async () => {
      try {
        const res = await axiosInstance.get(
          `/message/${initialValues.id}?limit=${limit}`
        );
        setMessages(res.data.data.sort((a, b) => a.id - b.id));
        setTotalCount(res.data.totalCount);
      } catch (error) {
        console.log(error);
      }
    };

    initialValues.id && findMessages();
  }, [initialValues.id, limit]);

  // --------------------------------- [ LOAD RECENT CHATS ] ------------------------

  const loadRecentChats = async () => {
    try {
      const res = await axiosInstance.get(
        `/message/${initialValues.id}?offset=${messages.length}&limit=${limit}`
      );
      setMessages((prev) => [
        ...res.data.data.sort((a, b) => a.id - b.id),
        ...prev,
      ]);
    } catch (error) {
      console.log(error);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && value) {
      HandleSendMessage();
    }
  };

  useEffect(() => {
    if (isOpen) {
      messageList.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (initialValues.message) {
      setValue(initialValues.message);
    }
  }, [initialValues.message]);

  const handleClose = () => {
    onClose();
    setMessages([]);
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[99999] bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-center justify-center px-3 sm:px-4 animate-fade-in"
        onClick={handleClose}
      >
        <div
          className="w-full max-w-[560px] h-[90vh] sm:h-[80vh] smallTablet:h-[85dvh] smallTablet:max-h-[90dvh] smallTablet:max-w-[calc(100vw-2rem)] bg-surface border border-edge rounded-2xl shadow-overlay overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ---------------------------------- [ HEADER ] ---------------------------------- */}
          <div className="flex px-5 py-4 smallTablet:px-4 smallTablet:py-3 justify-between items-center gap-4 border-b border-edge-faint shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faComments}
                  className="text-[15px] text-indigo-500"
                />
              </div>
              <div className="min-w-0">
                <h2 className="text-[15px] font-bold text-text-primary truncate capitalize">
                  {initialValues.title}
                </h2>
                <p className="text-[12px] font-medium text-light-gray">
                  Notes &amp; messages
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border border-edge text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="text-[14px]" />
            </button>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            {initialValues.isRejected && (
              <div className="flex items-start gap-2.5 px-5 py-3 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/25 shrink-0">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="text-[12px] mt-0.5 text-amber-600 dark:text-amber-300 shrink-0"
                />
                <p className="text-[13px] font-medium text-amber-700 dark:text-amber-300">
                  {initialValues.isRejected}
                </p>
              </div>
            )}

            {/* --------------------------------- [ MESSAGES ] --------------------------------- */}
            <div className="px-5 py-5 smallTablet:px-4 smallTablet:py-4 flex-1 overflow-y-auto custom-scrollbar">
              {totalCount - messages.length > 0 && (
                <div className="flex justify-center pb-4">
                  <button
                    type="button"
                    onClick={loadRecentChats}
                    className="h-8 px-4 rounded-full border border-edge text-[12px] font-semibold text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200"
                  >
                    Load earlier messages
                  </button>
                </div>
              )}

              {messages.length ? (
                <div className="flex flex-col gap-4">
                  {messages.map((item, idx) => {
                    const isCustomer = item.type === "portal";

                    return (
                      <div
                        className={clsx(
                          "flex flex-col max-w-[80%] mobile:max-w-[90%] smallTablet:break-words",
                          isCustomer ? "self-start" : "self-end items-end"
                        )}
                        key={item?.id ?? idx}
                      >
                        <div
                          className={clsx(
                            "px-3.5 py-2.5 text-[13px] font-medium leading-relaxed",
                            isCustomer
                              ? "bg-surface-muted border border-edge-faint text-text-primary rounded-2xl rounded-bl-md"
                              : "bg-gradient-to-r from-accent to-accent-bright text-white rounded-2xl rounded-br-md shadow-glow-sm"
                          )}
                        >
                          {item.message}
                        </div>
                        <p className="mt-1 px-1 text-[11px] font-medium text-light-gray tabular-nums">
                          {moment(item?.createdAt).format("DD MMM YYYY · HH:mm")}
                        </p>
                      </div>
                    );
                  })}
                  <div ref={messageList}></div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center gap-2">
                  <div className="h-11 w-11 rounded-xl bg-overlay-mid flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faComments}
                      className="text-[16px] text-light-gray"
                    />
                  </div>
                  <p className="text-[13px] font-medium text-light-gray">
                    No messages yet — start the conversation below.
                  </p>
                </div>
              )}
            </div>

            {/* --------------------------------- [ COMPOSER ] --------------------------------- */}
            <div className="px-5 py-4 smallTablet:px-4 smallTablet:py-3 border-t border-edge-faint shrink-0">
              <div className="h-11 pl-4 pr-1 bg-input-bg border border-input-border focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 rounded-full flex items-center gap-2 transition-all duration-200">
                <input
                  ref={messageInputRef}
                  type="text"
                  value={value}
                  className="flex-1 smallTablet:min-w-0 smallTablet:text-[16px] text-[13px] font-medium outline-none bg-transparent text-text-primary placeholder:text-light-gray"
                  placeholder="Write a message..."
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  onClick={HandleSendMessage}
                  disabled={!value || loading}
                  title="Send message"
                  className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-white bg-gradient-to-r from-accent to-accent-bright shadow-glow-sm hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <FontAwesomeIcon
                    icon={loading ? faCircleNotch : faArrowUp}
                    className={clsx("text-[13px]", loading && "fa-spin")}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default MessageModel;
